import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Play, Pause, Maximize, Volume2, VolumeX, Minimize } from 'lucide-react';
import { getRedGifsEmbedUrl, getRedGifsThumbnail, getRedGifsVideoUrl, isRedGifsUrl } from '@/lib/utils';
import { COLORS } from '@/data';

interface VideoPlayerProps {
  url: string | null | undefined;
  title?: string;
  className?: string;
  showThumbnail?: boolean;
  onClick?: () => void;
  hideBranding?: boolean;
  initialMuted?: boolean;
}

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  title,
  className = '',
  showThumbnail = false,
  onClick,
  hideBranding = true,
  initialMuted = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [showControls, setShowControls] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const controlsTimer = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const thumbnail = getRedGifsThumbnail(url);
  const videoSrc = getRedGifsVideoUrl(url);
  const isRedGifs = isRedGifsUrl(url);

  // For non-RedGifs URLs (Google Drive etc), fall back to iframe
  const embedUrl = !isRedGifs ? getRedGifsEmbedUrl(url) : null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedProgress = duration > 0 ? (buffered / duration) * 100 : 0;

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    resetControlsTimer();
  };

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  };

  // Track fullscreen changes
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Seek via progress bar click/drag
  const seekToPosition = useCallback((clientX: number) => {
    if (!progressRef.current || !videoRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    videoRef.current.currentTime = fraction * duration;
    setCurrentTime(fraction * duration);
  }, [duration]);

  const handleSeekStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSeeking(true);
    seekToPosition(e.clientX);
  };

  useEffect(() => {
    if (!isSeeking) return;
    const handleMove = (e: MouseEvent) => seekToPosition(e.clientX);
    const handleUp = () => setIsSeeking(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isSeeking, seekToPosition]);

  // Touch seek support
  const handleTouchSeek = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    if (touch) {
      seekToPosition(touch.clientX);
    }
  };

  const resetControlsTimer = () => {
    setShowControls(true);
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  // Thumbnail mode - clickable preview
  if (showThumbnail && onClick) {
    return (
      <button
        onClick={onClick}
        className={`relative w-full aspect-video bg-zinc-900 rounded-xl overflow-hidden group ${className}`}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={title || 'Video thumbnail'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition-colors">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: COLORS.lime }}
          >
            <Play className="w-8 h-8 text-zinc-900 ml-1" />
          </div>
        </div>
        {title && (
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-sm font-medium truncate">{title}</p>
          </div>
        )}
      </button>
    );
  }

  // Native video player for RedGifs (streams through our proxy)
  if (isRedGifs && videoSrc && !videoError) {
    return (
      <div
        ref={containerRef}
        className={`relative aspect-video bg-black rounded-xl overflow-hidden group ${className}`}
        onMouseMove={resetControlsTimer}
        onMouseEnter={() => setShowControls(true)}
      >
        <video
          ref={videoRef}
          src={videoSrc}
          poster={thumbnail || undefined}
          className="w-full h-full object-contain"
          muted={isMuted}
          playsInline
          loop
          preload="none"
          onClick={handlePlayPause}
          onPlay={() => { setIsPlaying(true); setIsLoading(false); }}
          onPause={() => setIsPlaying(false)}
          onWaiting={() => setIsLoading(true)}
          onCanPlay={() => setIsLoading(false)}
          onTimeUpdate={() => {
            if (videoRef.current) {
              setCurrentTime(videoRef.current.currentTime);
            }
          }}
          onDurationChange={() => {
            if (videoRef.current && isFinite(videoRef.current.duration)) {
              setDuration(videoRef.current.duration);
            }
          }}
          onProgress={() => {
            if (videoRef.current && videoRef.current.buffered.length > 0) {
              setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
            }
          }}
          onError={(e) => {
            console.error('VideoPlayer native error:', e.currentTarget.error?.code, e.currentTarget.error?.message, 'src:', videoSrc);
            setVideoError(true);
          }}
        />

        {/* Loading spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 border-3 border-white/20 border-t-lime-400 rounded-full animate-spin" />
          </div>
        )}

        {/* Play button overlay when paused */}
        {!isPlaying && !isLoading && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
            onClick={handlePlayPause}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
              style={{ backgroundColor: COLORS.lime }}
            >
              <Play className="w-8 h-8 text-zinc-900 ml-1" />
            </div>
          </div>
        )}

        {/* Title overlay top-left */}
        {title && !isPlaying && (
          <div className="absolute top-0 left-0 right-0 p-3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
            <p className="text-white text-sm font-medium truncate">{title}</p>
          </div>
        )}

        {/* Controls bar */}
        <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent transition-opacity duration-200 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>
          {/* Seek bar */}
          <div
            ref={progressRef}
            className="relative w-full h-6 px-3 flex items-center cursor-pointer group/seek"
            onMouseDown={handleSeekStart}
            onTouchStart={handleTouchSeek}
            onTouchMove={handleTouchSeek}
          >
            {/* Track background */}
            <div className="w-full h-1 group-hover/seek:h-1.5 bg-white/20 rounded-full transition-all relative">
              {/* Buffered */}
              <div
                className="absolute top-0 left-0 h-full bg-white/30 rounded-full"
                style={{ width: `${bufferedProgress}%` }}
              />
              {/* Progress */}
              <div
                className="absolute top-0 left-0 h-full rounded-full"
                style={{ width: `${progress}%`, backgroundColor: COLORS.lime }}
              />
              {/* Seek handle */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 group-hover/seek:opacity-100 transition-opacity shadow-md"
                style={{ left: `${progress}%`, marginLeft: '-6px', backgroundColor: COLORS.lime }}
              />
            </div>
          </div>

          {/* Button row */}
          <div className="flex items-center justify-between px-3 pb-2">
            <div className="flex items-center gap-2">
              <button onClick={handlePlayPause} className="text-white hover:text-lime-400 p-1 transition-colors">
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button onClick={handleMuteToggle} className="text-white hover:text-lime-400 p-1 transition-colors">
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              {/* Time display */}
              <span className="text-white/70 text-xs font-mono ml-1 select-none">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleFullscreen} className="text-white hover:text-lime-400 p-1 transition-colors">
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Iframe fallback for non-RedGifs OR if native player errors
  const iframeSrc = isRedGifs ? getRedGifsEmbedUrl(url) : embedUrl;
  if (iframeSrc) {
    return (
      <div className={`relative aspect-video bg-black rounded-xl overflow-hidden ${className}`}>
        <iframe
          src={iframeSrc}
          className="w-full h-full"
          frameBorder="0"
          scrolling="no"
          allowFullScreen
          allow="fullscreen; autoplay"
        />
        {hideBranding && (
          <>
            <div className="absolute bottom-0 left-0 w-28 h-12 bg-gradient-to-tr from-black via-black/80 to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 w-36 h-14 bg-gradient-to-br from-black via-black/70 to-transparent pointer-events-none" />
          </>
        )}
      </div>
    );
  }

  // Fallback when no URL
  return (
    <div className={`aspect-video bg-zinc-900 rounded-xl flex items-center justify-center ${className}`}>
      <div className="text-center text-zinc-500">
        <Play className="w-12 h-12 mx-auto mb-2" style={{ color: COLORS.lime }} />
        <p className="text-sm">No video available</p>
      </div>
    </div>
  );
};
